import React, { useState, useReducer, useCallback } from 'react';
import { Container } from 'reactstrap';
import { RouteComponentProps } from 'react-router';
import { Routes } from "../../../Routes";
import { ViewMode } from '../../../components/Common/ViewMode';
import { ParameterNewEdit, ParameterDuplicateList } from '../ParameterNewEdit';
import { Messages, EhpcMessage, msgReducer } from '../../../components/Common';
import { ParameterFormValues, useParameterContext } from '../../../components/Parameter';
import { ParameterService } from '../../../api';
import { EhpcModal } from '../../../components/Common/Modals';
import { DuplicateList } from '../../../api/dto';
import { CreateWorkflow } from '../../../components/Common/CreateWorkflow';
import { ServiceError } from '../../../api/errors';


interface ParameterViewMatchProps {
  parameterId: string;
}
interface BaseParameterViewProps {
  viewMode: ViewMode;
}

type ParameterViewProps = BaseParameterViewProps & RouteComponentProps<ParameterViewMatchProps>;
/**
 * 
 * @author Jungwirth
 */
export const ParameterView: React.FC<ParameterViewProps> = (props: Readonly<ParameterViewProps>): JSX.Element => {
  const { viewMode, match: { params: { parameterId }}, history } = props;
  const { setMessage, setResetForm } = useParameterContext();
  const [messages, dispatch] = useReducer(msgReducer, []);
  const [denyModal, setDenyModal] = useState(false);
  const [formValues, setFormValues] = useState<ParameterFormValues>();
  const [ duplicates, setDuplicates ] = useState<DuplicateList | undefined>(undefined);
  const [workflow, setWorkflow] = useState<CreateWorkflow>(CreateWorkflow.CheckDuplicates);

  /**
   * service call to deny a parameter and set result messages
   */
  const onHandleDeny = async (): Promise<void> => {
    console.log('deny parameter', formValues);
    try{
      const pId = Number(parameterId) || 0;
      const result = await ParameterService.denyParameter(pId, formValues?.anforderung.anmerkung ?? '');
      console.log('parameter-deny-result',result);
      setMessage({id: undefined, color: 'success',
          messageId: 'parameter_deny_success',
          messageValues: { name: formValues?.name}});
      // timeout due to rendering issues with modal window on this page
          setTimeout(() => {
        history.push(Routes.ParameterMgmt);
      }, 200);
    }
    catch(e) {
      console.log('parameter-deny-error', e);
      dispatch({type: 'add', payload: 
      {id: undefined, color: 'danger', 
        messageId: 'parameter_deny_error'}});
    }
  }

  /**
   * 
   */
  const onHandleSubmit = useCallback(async (values: ParameterFormValues, args: any) => {
    console.log('handle submit', values, args);
    setFormValues(values);

    if (args === 'newedit') {
      try{
        if (viewMode === ViewMode.New) {
          console.log('create parameter');
          const result = await ParameterService.createParameter(values);
          console.log(result);
          if(result) {
            setMessage({id: undefined, color: 'success', messageId: 'parameter_create_success', messageValues: {name: values?.name }});
          } 
        }
        if (viewMode === ViewMode.Edit) {
          const paramId = Number(parameterId) || 0;
          const result = await ParameterService.editParameter(paramId, values);
          if(result) {
            setMessage({id: undefined, color: 'success', messageId: 'parameter_edit_success', messageValues: {name: values?.name }});
          } 
          console.log('update parameter', result);
        }
        history.push(Routes.ParameterMgmt);

      }
      catch(e) {
        console.log('create parameter error', e);
        if(e instanceof ServiceError) {
          if(e.status === 400) {
            dispatch({ type: 'add', payload: {
              id: undefined, 
              color: "danger", 
              messageId: 'parameter_create_error', 
              messageValues: {action: viewMode === ViewMode.New ? 'Erstellen' : 'Bearbeiten', details: e.response.message } 
            }});
          } else {
            dispatch({ type: 'add',
              payload: {id: undefined,
              color: 'danger',
              messageId: 'parameter_create_error', 
              messageValues: {action: viewMode === ViewMode.New ? 'Erstellen' : 'Bearbeiten', details: e.response.message }}});

          }
        } else {
          dispatch({ type: 'add',
          payload: {id: undefined,
            color: 'danger',
            messageId: 'parameter_create_error', 
            messageValues: {action: viewMode === ViewMode.New ? 'Erstellen' : 'Bearbeiten', details: 'Unerwarteter Fehler'}}});
        }
      }
      
    }
    if (args === 'deny') {
      setDenyModal(true);
    }
    if (args === 'duplicate') {
      console.log('check duplicates');
      setDuplicates(undefined);
      try {
        const result = await ParameterService.checkDuplicates(values, viewMode === ViewMode.Edit ? Number(parameterId) : undefined);
        console.log('duplicate result', result);
        setDuplicates(result.duplicates);
      } catch(e) {
        setWorkflow(CreateWorkflow.CheckDuplicates);
        setDuplicates(undefined);
        dispatch({ type: 'add', payload: {
          id: undefined, 
          color: "danger", 
          messageId: 'duplicatecheck_error', 
          messageValues: { details: '' } 
        }});
        console.log('duplicate error', e);
      }
    }

  },[parameterId, setMessage, viewMode, history]);

  return (
    <Container className="p-4" fluid>
      <div className="pl-4 pr-4">
        <Messages messages={messages} removeMessage={(m: EhpcMessage)=> dispatch({type: 'remove', payload: m}) } />
      </div>
      <ParameterNewEdit
        viewMode={viewMode}
        parameterId={parameterId}
        workflow={workflow}
        setWorkflow={setWorkflow}
        onSubmit={onHandleSubmit}
        onReset={() => {
          setDuplicates(undefined);
          setResetForm();
        }}
      />
      <EhpcModal 
        open={denyModal} setOpen={setDenyModal}
        headerId="modal_header_deny"
        messageId="parameter_modal_deny_msg"
        messageValues={{name: formValues?.name}}
        handleAccept={()=> {onHandleDeny()}}
      />
      <div className="pl-4 pr-4">
        {duplicates && 
          <ParameterDuplicateList duplicates={duplicates} />
        }
      </div>
    </Container>
  );
}
